/**
 * usePrescribingContext.ts — the supplies half of the prescribe form's reads.
 *
 * Owner repo: @thegpservice/gpsclinics-portal. Slice: prescription supplies
 * (2026-09-24).
 *
 * ═════════════════════════════════════════════════════════════════════════════
 * WHY THIS HOOK EXISTS, AND WHY IT FAILS LOUDLY — THE SAME POSTURE AS
 * `useFormulary`, FOR THE SAME KIND OF REASON.
 *
 * A live patient received her pens with nothing to use them with. The clinician
 * HAD written the requirement down — as free-text prescription notes, which never
 * reach the pharmacy's dispensing email and print in small grey type at the foot
 * of the PDF. The clinical lead's ruling: the pen supply goes with every
 * injectable prescription by default, and a sharps bin with a patient's first
 * prescription. Both are now SUGGESTED by orders-service
 * (`GET /v1/orders/:orderCaseId/prescribing-context`) and preloaded into the
 * form, where the clinician can remove either or add either to anything.
 *
 * SO A FAILED READ MUST BLOCK, NOT DEGRADE. Everywhere else in this portal a
 * failed list read degrades gracefully. Here, degrading means a form that says
 * nothing about supplies and issues a prescription without them — which is the
 * incident, reproduced by an outage. The form renders an error with a retry and
 * refuses to issue until this read succeeds.
 *
 * `suppliesAvailable: false` is the SAME failure one hop further away (orders
 * could not read the catalogue from prices-service), so it is folded into
 * `error` here rather than being rendered as an empty catalogue.
 * ═════════════════════════════════════════════════════════════════════════════
 *
 * R28: not one supply name exists in this file. Every name on this surface
 * arrives at runtime from the API. The pure rules (seeding, validation, the
 * wire shape) live in `supplyRows.ts`.
 */
import { useCallback, useEffect, useState } from 'react';
import { getPrescribingContext } from '../api/orders';
import type { PrescribingContext } from '../api/types';
import { seedSupplyRows, supplyQuantityValid, usableContext, type SupplyDraft } from './supplyRows';

/** What the prescribe form knows about this order's supplies. */
export type PrescribingContextState =
  | { state: 'loading' }
  | { state: 'ready'; context: PrescribingContext }
  | { state: 'error' };

/**
 * Read the prescribing context for ONE order case.
 *
 * A response `usableContext` refuses (`suppliesAvailable: false`, or a suggested
 * or prior key the catalogue cannot name) is folded into `error` here, so every
 * consumer blocks on it the same way.
 *
 * `reload` is exposed so the error state can offer a real retry: the clinician
 * is blocked until this read succeeds, so they must be able to try again without
 * losing the medication lines they have already filled in.
 */
export function usePrescribingContext(orderCaseId: string): {
  context: PrescribingContextState;
  reload: () => void;
} {
  const [context, setContext] = useState<PrescribingContextState>({ state: 'loading' });
  const [attempt, setAttempt] = useState(0);

  const reload = useCallback(() => setAttempt((n) => n + 1), []);

  useEffect(() => {
    let cancelled = false;
    setContext({ state: 'loading' });
    getPrescribingContext(orderCaseId)
      .then((response) => {
        if (cancelled) return;
        setContext(usableContext(response) ? { state: 'ready', context: response } : { state: 'error' });
      })
      .catch(() => {
        if (!cancelled) setContext({ state: 'error' });
      });
    return () => {
      cancelled = true;
    };
  }, [orderCaseId, attempt]);

  return { context, reload };
}

/**
 * THE SUPPLIES ROWS FOR ONE PRESCRIBING SURFACE — the read, the rows seeded
 * from it, and the two verdicts a submit control needs.
 *
 * ONE HOOK FOR BOTH SURFACES. `PrescribeForm` and `RePrescribeModal` both mount
 * a `SuppliesSection` over this, so neither can seed, validate or gate its
 * supplies differently from the other — which is how the modal came to ship
 * with no supplies at all while the form had them.
 *
 * `rows === null` means the rows have not been seeded yet, and the surface's
 * submit waits (`ready` is false). They are SEEDED ONCE, from `seedSupplyRows`
 * (the prior prescription's supplies first, then every suggestion that list
 * does not already carry; a supply on both keeps the prior quantity), the first
 * time the read succeeds — including after a Retry — and never re-seeded
 * afterwards: a late or repeated answer must not put back a supply the
 * clinician has just removed WITHIN this form. (Across re-issues it can: a
 * supply removed from one prescription is absent from the next one's prior
 * list, so while the server still suggests it — a patient still first-time is
 * still suggested a bin — it comes back as a suggestion, for the clinician to
 * remove again.)
 */
export function useSupplyRows(orderCaseId: string): {
  context: PrescribingContextState;
  reload: () => void;
  rows: SupplyDraft[] | null;
  setRows: (rows: SupplyDraft[]) => void;
  /** The read succeeded AND the rows were seeded from it. */
  ready: boolean;
  /** Ready, and every row's quantity is a whole number 1..50. */
  valid: boolean;
} {
  const { context, reload } = usePrescribingContext(orderCaseId);
  const [rows, setRows] = useState<SupplyDraft[] | null>(null);

  useEffect(() => {
    if (context.state !== 'ready') return;
    const seeded = seedSupplyRows(context.context);
    setRows((prev) => prev ?? seeded);
  }, [context]);

  const ready = context.state === 'ready' && rows !== null;
  const valid = ready && rows.every((row) => supplyQuantityValid(row.quantityText));
  return { context, reload, rows, setRows, ready, valid };
}
